import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { MarksSummary, QuestionReview, mcqScore, parseAnnotations, reviewUnitInfo } from '../components/AttemptReview';
import { useToast } from '../components/Toast';
import type { AnnotationMap, QuestionReviewInput, ReviewBundle, ReviewQuestion } from '../types/models';

/** Whether an annotation map holds any staff strokes. */
function hasStrokes(map: AnnotationMap): boolean {
  return Object.values(map).some(arr => arr && arr.length > 0);
}

/** Gradable unit ids for a question: each part for a multi-part question, else the question itself. */
function unitIdsOf(q: ReviewQuestion): string[] {
  return q.parts && q.parts.length > 0 ? q.parts.map(p => p.id) : [q.id];
}

/** Initial marks per gradable unit: a saved grade if present, else MCQ auto-score, else 0. */
function initialScores(qs: ReviewQuestion[]): Record<string, number> {
  const m: Record<string, number> = {};
  for (const q of qs) {
    if (q.parts && q.parts.length > 0) {
      for (const p of q.parts) m[p.id] = p.awardedPoints ?? 0;
    } else {
      m[q.id] = q.awardedPoints != null
        ? q.awardedPoints
        : (q.type !== 'Descriptive' ? mcqScore(q) : 0);
    }
  }
  return m;
}

export function ReviewAttemptPage() {
  const toast = useToast();
  const { examId, attemptId, orgId } = useParams();
  const nav = useNavigate();
  const reviewListPath = orgId ? `/org/${orgId}/exams/${examId}/review` : `/exam/${examId}/review`;
  const [bundle, setBundle] = useState<ReviewBundle | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Grading input state — initialised from the bundle once it loads.
  const [scores, setScores] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState<string>('');
  // Per-question red-pen corrections (keyed by question id → page-id → strokes) and remarks.
  const [annos, setAnnos] = useState<Record<string, AnnotationMap>>({});
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.getReviewBundle(attemptId!)
      .then(b => {
        setBundle(b);
        setScores(initialScores(b.questions));
        setNotes(b.graderNotes ?? '');
        const a: Record<string, AnnotationMap> = {};
        const r: Record<string, string> = {};
        for (const q of b.questions) {
          a[q.id] = parseAnnotations(q.annotationJson);
          if (q.graderRemark) r[q.id] = q.graderRemark;
        }
        setAnnos(a);
        setRemarks(r);
      })
      .catch(e => setError(e.message));
  }, [attemptId]);

  const setUnitScore = (unitId: string, points: number) => {
    setScores(s => ({ ...s, [unitId]: points }));
    setSaved(false);
  };
  const setQuestionAnnotation = (qid: string, map: AnnotationMap) => {
    setAnnos(a => ({ ...a, [qid]: map }));
    setSaved(false);
  };
  const setQuestionRemark = (qid: string, text: string) => {
    setRemarks(r => ({ ...r, [qid]: text }));
    setSaved(false);
  };

  async function resolveReeval() {
    if (!bundle) return;
    try {
      await api.resolveReevaluation(attemptId!);
      setBundle({ ...bundle, reevaluationStatus: 'Completed' });
    } catch (e: any) {
      alert(e?.message ?? 'Failed to mark re-evaluation done.');
    }
  }

  async function saveGrade() {
    if (!bundle) return;
    const total = bundle.questions
      .reduce((s, q) => s + unitIdsOf(q).reduce((t, id) => t + (scores[id] ?? 0), 0), 0);
    if (total > bundle.totalMarks) {
      alert(`The marks add up to ${total}, more than the exam total of ${bundle.totalMarks}. Adjust the per-question marks.`);
      return;
    }
    setSaving(true);
    setSaved(false);
    try {
      // One score per gradable unit (a part for multi-part questions, else the question).
      const questionScores = bundle.questions.flatMap(q =>
        unitIdsOf(q).map(id => ({ questionId: id, points: scores[id] ?? 0 })));
      // Per-question red-pen corrections + remarks. Send an entry when there's current content
      // OR the question previously had some (so clearing it persists).
      const reviews: QuestionReviewInput[] = bundle.questions.flatMap(q => {
        const map = annos[q.id] ?? {};
        const annotationJson = hasStrokes(map) ? JSON.stringify(map) : null;
        const remark = (remarks[q.id] ?? '').trim() || null;
        const hadContent = !!q.annotationJson || !!q.graderRemark;
        return (annotationJson || remark || hadContent)
          ? [{ questionId: q.id, annotationJson, remark }]
          : [];
      });
      const r = await api.gradeAttempt(attemptId!, questionScores, notes || null, reviews);
      setBundle({ ...bundle, scoreAwarded: r.scoreAwarded, graderNotes: notes || null });
      setSaved(true);
      toast.success('Evaluation saved.');
    } catch (e: any) {
      const msg = e?.message ?? 'Failed to save grade.';
      alert(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (error) return <div className="container"><div className="card error">{error}</div></div>;
  if (!bundle) return <div className="container"><div className="card muted">Loading submission…</div></div>;

  // Auto-score MCQs so the grader can see the tally.
  const autoScore = bundle.questions.reduce((sum, q) => sum + mcqScore(q), 0);
  const mcqMax = bundle.questions
    .filter(q => q.type !== 'Descriptive')
    .reduce((s, q) => s + q.marks, 0);
  const unitInfo = reviewUnitInfo(bundle.questions);

  // Score by gradable unit (part or question), and the rolled-up total scored for a whole question.
  const scoreOfUnit = (unitId: string) => scores[unitId] ?? 0;
  const questionScore = (q: ReviewQuestion) => unitIdsOf(q).reduce((s, id) => s + scoreOfUnit(id), 0);
  const scoreOf = (qid: string) => {
    const q = bundle.questions.find(x => x.id === qid);
    return q ? questionScore(q) : 0;
  };
  const gradedTotal = bundle.questions.reduce((s, q) => s + questionScore(q), 0);
  const sectionScored = (sectionId: string) =>
    bundle.questions.filter(q => q.sectionId === sectionId).reduce((s, q) => s + questionScore(q), 0);
  const grading = { valueOf: scoreOfUnit, onChange: setUnitScore };

  // One question card with its marks, red-pen annotation layer and remark hooks wired up.
  const renderQuestion = (q: ReviewQuestion) => (
    <QuestionReview
      key={q.id}
      index={(unitInfo.get(q.id)?.unit ?? 1) - 1}
      altLabel={unitInfo.get(q.id)?.alt}
      question={q}
      grading={grading}
      annotation={{ value: annos[q.id] ?? {}, onChange: m => setQuestionAnnotation(q.id, m) }}
      remark={{ value: remarks[q.id] ?? '', onChange: t => setQuestionRemark(q.id, t) }}
    />
  );

  return (
    <>
      <header className="topbar">
        <div className="brand">{bundle.examTitle} · Review</div>
        <div className="row" style={{ gap: 16 }}>
          <span style={{ fontSize: 13 }}>
            {bundle.studentName}{bundle.rollNumber ? ` · ${bundle.rollNumber}` : ''}
          </span>
          <button onClick={() => nav(reviewListPath)} className="topbar-ghost">
            ← Back
          </button>
        </div>
      </header>

      <div className="container">
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Status</div>
              <div>{bundle.status}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Started</div>
              <div>{new Date(bundle.startedAt).toLocaleString()}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Submitted</div>
              <div>{bundle.submittedAt ? new Date(bundle.submittedAt).toLocaleString() : '—'}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>MCQ auto-score</div>
              <div style={{ fontWeight: 600 }}>{autoScore} / {mcqMax}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Total marks</div>
              <div>{bundle.totalMarks}</div>
            </div>
          </div>
        </div>

        {bundle.reevaluationStatus !== 'None' && (
          <div className="card" style={{
            background: bundle.reevaluationStatus === 'Requested' ? 'var(--warn-soft-bg)' : 'var(--success-soft-bg)',
            color: bundle.reevaluationStatus === 'Requested' ? 'var(--warn-soft-fg)' : 'var(--success-soft-fg)'
          }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600 }}>
                  {bundle.reevaluationStatus === 'Requested' ? 'Re-evaluation requested' : 'Re-evaluation completed'}
                  {bundle.reevaluationRequestedAt ? ` · ${new Date(bundle.reevaluationRequestedAt).toLocaleString()}` : ''}
                </div>
                {bundle.reevaluationReason && (
                  <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{bundle.reevaluationReason}</div>
                )}
              </div>
              {bundle.reevaluationStatus === 'Requested' && (
                <button onClick={resolveReeval}>Mark re-evaluation done</button>
              )}
            </div>
          </div>
        )}

        {bundle.sections.length > 0 ? bundle.sections.map(sec => (
          <div key={sec.id}>
            <div className="card" style={{ background: 'var(--surface-2)' }}>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h3 style={{ margin: 0 }}>{sec.title}</h3>
                <span style={{ fontWeight: 600 }}>{sectionScored(sec.id)} / {sec.maxMarks}</span>
              </div>
              {sec.instructions && (
                <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{sec.instructions}</div>
              )}
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                {sec.requiredAnswers > 0
                  ? `Required: ${sec.requiredAnswers} of ${sec.questionCount}`
                  : `Required: all ${sec.questionCount}`} · max {sec.maxMarks} marks
              </div>
            </div>
            {bundle.questions
              .filter(q => q.sectionId === sec.id)
              .map(renderQuestion)}
          </div>
        )) : (
          bundle.questions.map(renderQuestion)
        )}

        {bundle.sections.length > 0 && bundle.questions.some(q => !q.sectionId) && (
          <>
            <div className="card" style={{ background: 'var(--surface-2)' }}>
              <h3 style={{ margin: 0 }}>Other questions</h3>
            </div>
            {bundle.questions.filter(q => !q.sectionId).map(renderQuestion)}
          </>
        )}

        <MarksSummary bundle={bundle} scoreOf={scoreOf} />

        <div className="card">
          <h3 style={{ marginTop: 0 }}>Final grade</h3>
          <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
            <div style={{ flex: '0 0 160px' }}>
              <div className="muted">Total score</div>
              <div style={{ fontSize: 22, fontWeight: 700,
                   color: gradedTotal > bundle.totalMarks ? 'var(--danger)' : undefined }}>
                {gradedTotal} / {bundle.totalMarks}
              </div>
            </div>
            <label style={{ flex: 1 }}>
              <div className="muted">Notes (optional)</div>
              <input
                value={notes}
                onChange={e => { setNotes(e.target.value); setSaved(false); }}
                placeholder="Remarks for the student or record"
              />
            </label>
            <button className="primary" onClick={saveGrade} disabled={saving || gradedTotal > bundle.totalMarks}>
              {saving ? 'Saving…' : 'Save grade'}
            </button>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
            Total is the sum of the per-question marks above. MCQs are pre-filled with their auto-score
            (use “auto” to reset); enter marks for each descriptive question.
          </div>
          {saved && <div className="success" style={{ marginTop: 8 }}>Grade saved.</div>}
        </div>
      </div>
    </>
  );
}

